import chainOfResponsibility from './chain-of-responsibility.jpg'
import server from './server.jpg'

const content = `<p>The Chain of Responsibility pattern is useful when there are several objects that can handle requests.</p>

<p>Let’s call those objects handlers. In a basic form of the pattern, each handler is able to satisfy some types of requests, but not all requests. For a given request, there is only a subset of handlers (or even just one) that can handle it.</p>

<p>Where to put the code that knows which handler can satisfy which requests?</p>

<p>The Chain of Responsibility pattern consists in putting that code in the handler themselves: each handler knows if it can handle a given request. And if it doesn’t, it forwards the request to another handler.</p>

<p>The handlers form a chain, that the request traverses until reaching one handler that can satisfy it:</p>

<img src=${chainOfResponsibility} alt="chain-of-responsibility" />

<p>By decoupling the caller from the callee, Chain of Responsibility achieves <a href="/indirection">Indirection</a>.</p>

<p>The Chain of Responsibility pattern can have some variations where handlers can partially treat the request before passing them to the next handler in the chain.</p>

<p>An example of such a Chain of Responsibility can be found in Rails, where the server can configure a middleware that consists of an array of classes through which HTTP requests go through when they arrive on the server. Each class can do some treatment and forward (or not) the HTTP request to the next class in the array, until the request reaches the controller that treats it.</p>

<p>Here are some examples of what those middleware classes can do:</p>

<ul><li>block the request if it’s coming at a too high frequency (<a href="https://github.com/rack/rack-attack">rack-attack</a>)</li><li>redirect to an HTTPS url if the url is HTTP (<a href="https://api.rubyonrails.org/v7.0.3/classes/ActionDispatch/SSL.html">ActionDispatch::SSL</a>)</li><li>instrument the request to do some profiling</li></ul>

<p>Then the request is routed to a controller based on its verb and url.</p>

<img src=${server} alt="server" />

<p>What’s more, if all the handlers share the same interface to accept a request, each handler can call the next one on this interface without knowing which type of handler it is exactly. This usage of <a href="/polymorphism">Polymorphism</a> allows to change the order of handlers or add new ones without changing the code of existing handlers in the chain.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Chain of Responsibility consists of a list of handlers that can handle a request</li><li>Each handler can treat the request partially, completely or not at all</li><li>Each handler can choose to forward the request to the next</li></ul>
</div>`

export default content
